import React from "react";
import FilterHeader from "@/components/campaigns/FilterHeader";
import ScrollArea from "@/components/ui/shared/ScrollArea";
import Table from "@/components/ui/shared/table/Table";
import TableBody from "@/components/ui/shared/table/TableBody";
import TableCell from "@/components/ui/shared/table/TableCell";
import TableHeader from "@/components/ui/shared/table/TableHeader";
import TableRow from "@/components/ui/shared/table/TableRow";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import CampaignEdit from "@/components/campaigns/CampaignEdit";
import { useCampaigns } from "@/hooks/useCampaigns";
import { useAssistants } from "@/hooks/useAssistants";
import { usePipelines } from "@/hooks/usePipelines";
import { useTags } from "@/hooks/useTags";
import { useTeam, type TeamMember } from "@/hooks/useTeam";
import { useState, useEffect, memo, useMemo } from "react";
import { Megaphone } from "lucide-react";
import { formatDateInAccountTz } from "@/lib/accountTimezone";
import { useTranslation } from "react-i18next";

interface CampaignsProps {}

type AccountCampaign = {
  id: string;
  account_id: string;
  name: string;
  assistant_id: string | null;
  trigger_type: string;
  trigger_keywords: string[] | null;
  tags: string[] | null;
  pipeline_id: string | null;
  pipeline_stage_id: string | null;
  is_active: boolean | null;
  distribution_member_ids: string[] | null;
  last_distribution_index: number | null;
  created_at: string | null;
  updated_at: string | null;
};

type Assistant = {
  id: string;
  name: string;
  is_active: boolean;
};

type Pipeline = {
  id: string;
  name: string;
};

type PipelineStage = {
  id: string;
  name: string;
  pipeline_id: string;
};

type CampaignUpdate = Partial<Omit<AccountCampaign, 'id' | 'account_id' | 'created_at' | 'updated_at'>>

type TagInfo = {
  name: string;
  color: string;
};

interface CampaignRowProps {
  campaign: AccountCampaign;
  assistants: Assistant[];
  pipelines: Pipeline[];
  pipelineStages: PipelineStage[];
  tagMap: Map<string, TagInfo>;
  teamMembers: TeamMember[];
  onToggleStatus: (id: string, currentStatus: boolean | null) => void;
  updateCampaign: (id: string, data: Partial<CampaignUpdate>) => Promise<boolean>;
  deleteCampaign: (id: string) => Promise<boolean>;
}

const CampaignRow = memo<CampaignRowProps>(({
  campaign,
  assistants,
  pipelines,
  pipelineStages,
  tagMap,
  teamMembers,
  onToggleStatus,
  updateCampaign,
  deleteCampaign
}) => {
  const { t } = useTranslation("automations");
  const getAssistantName = (assistantId: string | null) => {
    if (!assistantId) return t('campaign.noAssistant');
    const assistant = assistants.find(a => a.id === assistantId);
    return assistant ? assistant.name : 'Assistente não encontrado';
  };

  const getPipelineName = (pipelineId: string | null) => {
    if (!pipelineId) return 'Nenhum pipeline';
    const pipeline = pipelines.find(p => p.id === pipelineId);
    return pipeline ? pipeline.name : 'Pipeline não encontrado';
  };

  const getStageName = (stageId: string | null) => {
    if (!stageId) return 'Nenhuma etapa';
    const stage = pipelineStages.find(s => s.id === stageId);
    return stage ? stage.name : 'Etapa não encontrada';
  };

  const getTriggerDescription = (campaign: AccountCampaign) => {
    if (campaign.trigger_type === 'first_contact') {
      return 'Primeiro Contato';
    }

    if (campaign.trigger_type === 'any') {
      return 'Qualquer mensagem';
    }

    const typeText = campaign.trigger_type === 'contains' ? t('campaign.triggerContains') : t('campaign.triggerStartsWith');
    const keywords = campaign.trigger_keywords && campaign.trigger_keywords.length > 0
      ? campaign.trigger_keywords.slice(0, 2).join(', ') +
        (campaign.trigger_keywords.length > 2 ? '...' : '')
      : 'Nenhuma palavra-chave';

    return `${typeText}: ${keywords}`;
  };

  const assistantName = getAssistantName(campaign.assistant_id);
  const pipelineName = getPipelineName(campaign.pipeline_id);
  const stageName = getStageName(campaign.pipeline_stage_id);
  const triggerDescription = getTriggerDescription(campaign);
  const createdDate = campaign.created_at
    ? formatDateInAccountTz(campaign.created_at)
    : 'Data não disponível';

  const campaignTags = (campaign.tags || []).map((tagName) => {
    const info = tagMap.get(tagName.toLowerCase());
    return { name: tagName, color: info?.color || "#3B82F6" };
  });

  const distributionMembers = (campaign.distribution_member_ids || [])
    .map((id) => teamMembers.find((m) => m.id === id))
    .filter(Boolean) as TeamMember[];

  return (
    <TableRow className="border-b border-border/60 last:border-b-0 hover:bg-[#F8FAFC] transition-colors duration-200 dark:border-[#3F3F46]/50 dark:hover:bg-[#1f1f23]">
      <TableCell as="td" className="!py-4">
        <div className="flex flex-col">
          <p className="font-semibold text-[#0F172A] dark:text-neutral-50 dark:font-medium">{campaign.name}</p>
          <p className="text-xs text-[#64748B] dark:text-neutral-400">{t("common:shared.createdOnF", { date: createdDate })}</p>
        </div>
      </TableCell>

      <TableCell as="td" className="!py-4">
        <div className="flex flex-col">
          <span className={`text-sm ${campaign.assistant_id ? 'text-[#334155] dark:text-neutral-200' : 'text-[#64748B] dark:text-neutral-400'}`}>
            {assistantName}
          </span>
          {campaign.assistant_id && (
            <span className="text-[#64748B] dark:text-neutral-400 text-xs">
              {assistants.find(a => a.id === campaign.assistant_id)?.is_active ? t('campaign.active') : t('campaign.inactive')}
            </span>
          )}
        </div>
      </TableCell>

      <TableCell as="td" className="!py-4">
        <div className="flex flex-col">
          <span className="text-[#0F172A] dark:text-neutral-50 text-sm">{triggerDescription}</span>
          {campaign.trigger_keywords && campaign.trigger_keywords.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {campaign.trigger_keywords.slice(0, 3).map((keyword: string, index: number) => (
                <Badge key={index} variant="tag" className="text-xs">
                  {keyword}
                </Badge>
              ))}
              {campaign.trigger_keywords.length > 3 && (
                <Badge variant="tag" className="text-xs">
                  +{campaign.trigger_keywords.length - 3}
                </Badge>
              )}
            </div>
          )}
        </div>
      </TableCell>

      <TableCell as="td" className="!py-4">
        {campaignTags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {campaignTags.slice(0, 3).map((tag, i) => (
              <span
                key={i}
                className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium capitalize bg-[#DBEAFE] text-[#2563EB] border border-blue-200 dark:bg-blue-500/20 dark:text-[#60A5FA] dark:border-blue-500/30"
              >
                {tag.name}
              </span>
            ))}
            {campaignTags.length > 3 && (
              <span className="text-xs text-[#64748B] dark:text-neutral-400">+{campaignTags.length - 3}</span>
            )}
          </div>
        ) : (
          <span className="text-[#94A3B8] dark:text-neutral-500 text-sm">—</span>
        )}
      </TableCell>

      <TableCell as="td" className="!py-4">
        {distributionMembers.length > 0 ? (
          <div className="flex items-center">
            <div className="flex -space-x-1.5">
              {distributionMembers.slice(0, 3).map((m) => (
                <div
                  key={m.id}
                  className="w-6 h-6 rounded-full bg-[#2563EB] border-2 border-white flex items-center justify-center text-[9px] font-medium text-white dark:border-[#27272A]"
                  title={m.name || m.email}
                >
                  {m.name ? m.name.split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") : "?"}
                </div>
              ))}
            </div>
            {distributionMembers.length > 3 && (
              <span className="text-xs text-[#64748B] dark:text-neutral-400 ml-1.5">+{distributionMembers.length - 3}</span>
            )}
          </div>
        ) : (
          <span className="text-[#94A3B8] dark:text-neutral-500 text-sm">—</span>
        )}
      </TableCell>

      <TableCell as="td" className="!py-4">
        <div className="flex flex-col">
          <span className="text-[#0F172A] dark:text-neutral-50 text-sm font-medium">{pipelineName}</span>
          {campaign.pipeline_stage_id && (
            <span className="text-[#64748B] dark:text-neutral-400 text-xs">{stageName}</span>
          )}
        </div>
      </TableCell>

      <TableCell as="td" className="!py-4">
        <div className="flex items-center gap-2">
          <div
            className={`w-[7px] h-[7px] rounded-full ${
              campaign.is_active ? "bg-[#22C55E]" : "bg-[#94A3B8] dark:bg-neutral-500"
            }`}
          />
          <span className={`text-sm font-medium ${
            campaign.is_active
              ? "text-[#16A34A] dark:text-[#22C55E]"
              : "text-[#64748B] dark:text-neutral-400"
          }`}>
            {campaign.is_active ? t("campaign.activeF") : t("campaign.inactiveF")}
          </span>
        </div>
      </TableCell>

      <TableCell as="td" className="!py-4">
        <Switch
          checked={campaign.is_active || false}
          onCheckedChange={() => onToggleStatus(campaign.id, campaign.is_active)}
          activeColor="data-[state=checked]:bg-[#16A34A] dark:data-[state=checked]:bg-[#22C55E]"
        />
      </TableCell>

      <TableCell as="td" className="!py-4">
        <CampaignEdit campaign={campaign} updateCampaign={updateCampaign} deleteCampaign={deleteCampaign} />
      </TableCell>
    </TableRow>
  );
});

CampaignRow.displayName = 'CampaignRow';

const Campaigns: React.FC<CampaignsProps> = () => {
  const { t } = useTranslation("automations");
  const { campaigns, loading, error, toggleCampaignStatus, createCampaign, updateCampaign, deleteCampaign } = useCampaigns();
  const { assistants } = useAssistants();
  const { pipelines, pipelineStages } = usePipelines();
  const { tags: accountTags } = useTags();
  const { members: teamMembers } = useTeam();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCampaigns, setFilteredCampaigns] = useState(campaigns);

  const tagMap = useMemo(() => {
    const map = new Map<string, TagInfo>();
    accountTags.forEach((t) => map.set(t.name.toLowerCase(), { name: t.name, color: t.color }));
    return map;
  }, [accountTags]);

  // Filter campaigns based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCampaigns(campaigns);
      return;
    }

    const filtered = campaigns.filter(campaign => {
      const name = campaign.name.toLowerCase();
      const search = searchTerm.toLowerCase();

      return name.includes(search) ||
             campaign.trigger_keywords?.some((keyword: string) =>
               keyword.toLowerCase().includes(search)
             );
    });

    console.log('🔍 [Campaigns] Filtering campaigns:', {
      searchTerm,
      total_campaigns: campaigns.length,
      filtered_count: filtered.length
    });

    setFilteredCampaigns(filtered);
  }, [searchTerm, campaigns]);

  const handleToggleStatus = async (id: string, currentStatus: boolean | null) => {
    await toggleCampaignStatus(id, !currentStatus);
  };

  if (loading) {
    return (
      <div className="w-full flex h-[calc(100dvh-120px)] flex-col">
        <div className="mb-2 pb-4 border-b border-border">
          <h2 className="text-[26px] sm:text-[30px] font-bold text-[#0F172A] dark:text-neutral-50">{t("campaign.listTitle")}</h2>
        </div>
        <FilterHeader onSearchChange={setSearchTerm} createCampaign={createCampaign} />
        <div className="flex-1 min-h-0 mt-4 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-4 border-[#E5E7EB] border-t-[#2563EB] rounded-full animate-spin dark:border-neutral-600 dark:border-t-[#60A5FA]" />
            <p className="text-muted-foreground dark:text-neutral-50">{t("campaign.listLoading")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full flex h-[calc(100dvh-120px)] flex-col">
        <div className="mb-2 pb-4 border-b border-border">
          <h2 className="text-[26px] sm:text-[30px] font-bold text-[#0F172A] dark:text-neutral-50">{t("campaign.listTitle")}</h2>
        </div>
        <FilterHeader onSearchChange={setSearchTerm} createCampaign={createCampaign} />
        <div className="flex-1 min-h-0 mt-4 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-red-500/20">
              <svg className="w-8 h-8 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-[#0F172A] mb-2 dark:text-neutral-50">{t("campaign.listError")}</h3>
            <p className="text-[#64748B] text-sm dark:text-neutral-300">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex h-[calc(100dvh-120px)] flex-col">
      <div className="mb-2 pb-4 border-b border-border">
        <h2 className="text-[26px] sm:text-[30px] font-bold text-[#0F172A] dark:text-neutral-50">{t("campaign.listTitle")}</h2>
      </div>
      <FilterHeader onSearchChange={setSearchTerm} createCampaign={createCampaign} />
      <div className="flex-1 min-h-0 mt-4">
        {campaigns.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center border border-border rounded-[20px] bg-white shadow-sm dark:border-[#3F3F46] dark:rounded-[12px] dark:bg-[#27272A] dark:shadow-none">
            <div className="flex flex-col items-center gap-5 text-center p-8 max-w-md">
              <div className="w-16 h-16 bg-[#DBEAFE] rounded-full flex items-center justify-center dark:bg-blue-500/20">
                <Megaphone className="w-8 h-8 text-[#2563EB] dark:text-[#60A5FA]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2 dark:text-neutral-50">{t("campaign.listEmpty")}</h3>
                <p className="text-[#64748B] text-sm dark:text-neutral-400">
                  {t("campaign.listEmptyHint")}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <ScrollArea
            classNames={{
              root: "w-full h-full overflow-x-auto border border-border rounded-[20px] bg-white shadow-sm dark:border-[#3F3F46] dark:rounded-[12px] dark:bg-[#27272A] dark:shadow-none",
            }}
          >
            <Table className="min-w-[1100px]">
              <TableHeader className="border-b border-border dark:border-[#3F3F46]">
                <TableRow>
                  {[t("campaigns.thCampaign"), t("campaigns.thAssistant"), t("campaigns.thTrigger"), t("campaigns.thTags"), t("campaigns.thTeam"), t("campaigns.thPipeline"), t("campaigns.thStatus"), t("campaigns.thActive"), t("campaigns.thActions")].map((item, index) => (
                    <TableCell key={index} as="th" className="!text-[#475569] !font-semibold dark:!text-neutral-400">
                      <div className="flex justify-start items-center gap-1">
                        <p className="py-2">{item}</p>
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCampaigns.map((campaign) => (
                  <CampaignRow
                    key={campaign.id}
                    campaign={campaign}
                    assistants={assistants}
                    pipelines={pipelines}
                    pipelineStages={pipelineStages}
                    tagMap={tagMap}
                    teamMembers={teamMembers}
                    onToggleStatus={handleToggleStatus}
                    updateCampaign={updateCampaign}
                    deleteCampaign={deleteCampaign}
                  />
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </div>
    </div>
  );
};

export default Campaigns;
